// src/services/errorService.js

/**
 * Logs errors and returns user-friendly messages
 * @param {Error} error - The error object
 * @param {Object} context - Additional context about where/how the error occurred
 * @returns {string} User-friendly error message
 */
export const logError = (error, context = {}) => {
  // Log to console in development
  console.error('Error:', error, 'Context:', context);
  
  // Special handling for active job errors - log the ID explicitly
  if (error.code === 'active-job-exists' && error.activeJobId) {
    console.error(`Active Job ID causing this error: ${error.activeJobId}`);
  }
  
  // For production, send to a service like Sentry
  if (process.env.NODE_ENV === 'production') {
    // Sentry.captureException(error, { extra: context });
  }
  
  // Return user-friendly message
  return getErrorMessage(error);
};

/**
 * Maps technical error codes to user-friendly messages
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  // Firebase Authentication error codes
  const authErrorMap = {
    'auth/email-already-in-use': 'This email is already registered.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/weak-password': 'The password does not meet the requirements.',
    'auth/user-not-found': 'Invalid email or password.',
    'auth/wrong-password': 'Invalid email or password.',
    'auth/account-exists-with-different-credential': 'An account already exists with the same email address but different sign-in credentials.',
    'auth/too-many-requests': 'Too many unsuccessful login attempts. Please try again later or reset your password.',
    'auth/operation-not-allowed': 'This operation is not allowed. Please contact support.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/requires-recent-login': 'This operation requires recent authentication. Please log in again before retrying.',
    'auth/popup-closed-by-user': 'The authentication popup was closed before completing the sign-in process.',
    'auth/cancelled-popup-request': 'This operation has been cancelled due to another conflicting popup being opened.',
    'auth/invalid-action-code': 'The action code is invalid. This can happen if the code is malformed, expired, or has already been used.',
    'auth/provider-already-linked': 'Your account is already linked with another provider.',
    'auth/credential-already-in-use': 'This credential is already associated with a different user account.',
    'auth/auth-domain-config-required': 'Authentication domain configuration is required for this operation.',
    'auth/microsoft-auth-cancelled': 'Microsoft authentication was cancelled.',
  };
  
  // Firestore error codes
  const firestoreErrorMap = {
    'permission-denied': 'You do not have permission to perform this operation.',
    'not-found': 'The requested document was not found.',
    'already-exists': 'The document already exists.',
    'failed-precondition': 'The operation failed because a condition was not met.',
    'aborted': 'The operation was aborted.',
    'out-of-range': 'The operation was attempted past the valid range.',
    'unavailable': 'The service is currently unavailable. Please try again later.',
    'resource-exhausted': 'System resource limits have been exceeded.',
    'cancelled': 'The operation was cancelled.',
    'unknown': 'An unknown error occurred. Please try again.',
  };
  
  // Network error codes
  const networkErrorMap = {
    'network-error': 'Unable to connect to the server. Please check your internet connection.',
    'timeout': 'The request timed out. Please try again.',
  };
  
  // Application specific error codes
  const appErrorMap = {
    'search/invalid-input': 'Invalid search input. Please enter a valid name or identifier.',
    'search/no-results': 'No matches found for your search.',
    'search/too-many-results': 'Your search returned too many results. Please be more specific.',
    'validation/password-weak': 'Password does not meet security requirements.',
    'validation/input-sanitized': 'Your input contained invalid characters that were removed.',
    'SEARCH_LIMIT_EXCEEDED': 'Monthly search limit exceeded. Please upgrade your plan or wait for next month to continue searching.',
    'batch-insufficient-capacity': 'Insufficient search capacity for this batch. Please reduce batch size or wait for your limit to reset.',
  };
  
  // Combine all error maps
  const errorMap = {
    ...authErrorMap,
    ...firestoreErrorMap,
    ...networkErrorMap,
    ...appErrorMap,
  };
  
  // Extract error code or use error message directly
  const errorCode = error.code || '';
  const errorMessage = error.message || '';
  
  // Return the mapped message or a fallback
  if (errorCode && errorMap[errorCode]) {
    // For SEARCH_LIMIT_EXCEEDED, use the specific message if available
    if (errorCode === 'SEARCH_LIMIT_EXCEEDED' && errorMessage) {
      return errorMessage;
    }
    return errorMap[errorCode];
  } else if (errorCode === 'batch-size-limit-exceeded') {
    // This error will be handled specially by the UI component
    // Create a special format that the UI can recognize and transform
    return {
      type: 'BATCH_SIZE_LIMIT',
      nameCount: error.nameCount || 0,
      // Include the raw message for the console display
      rawMessage: `Batch size limit exceeded: Your file contains ${error.nameCount || 0} names, but the maximum allowed is 250. Please split your data into smaller batches or reduce the number of names in your file.`
    };
  } else if (errorCode === 'active-job-exists') {
    // Special format for active job error that can be handled by the UI
    return {
      type: 'ACTIVE_JOB_EXISTS',
      // Include job ID in raw message for console debugging only
      rawMessage: error.activeJobId 
        ? `You already have an active batch job (ID: ${error.activeJobId}). Please wait for it to complete before starting a new one.`
        : 'You already have an active batch job. Please wait for it to complete before starting a new one.'
    };
  } else if (errorMessage.includes('Batch size limit exceeded')) {
    // For backwards compatibility with any other batch size errors
    return {
      type: 'BATCH_SIZE_LIMIT',
      nameCount: 0,
      rawMessage: errorMessage
    };
  } else if (errorMessage.includes('already have an active batch job')) {
    // Handle active job error messages
    return {
      type: 'ACTIVE_JOB_EXISTS',
      rawMessage: errorMessage
    };
  } else if (errorMessage.toLowerCase().includes('batch') && 
           (errorMessage.toLowerCase().includes('limit') || 
            errorMessage.toLowerCase().includes('maximum') || 
            errorMessage.toLowerCase().includes('exceeded'))) {
    // Same approach for other batch-related errors
    return {
      type: 'BATCH_SIZE_LIMIT',
      nameCount: 0,
      rawMessage: errorMessage
    };
  } else if (errorMessage.includes('network')) {
    return 'A network error occurred. Please check your connection and try again.';
  } else if (errorMessage.includes('timeout')) {
    return 'The request timed out. Please try again.';
  } else if (errorMessage.includes('PERMISSION_DENIED')) {
    return 'You do not have permission to perform this operation.';
  }
  
  // For other errors, return a generic user-friendly message
  // instead of the often technical original error
  return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
};